<template>
  <b-row class="timesheets-details mt-3">
    <b-col class="xxl-9 overflow-auto">
      <calendar
        class="calendar"
        :masks="$screens({ default: {weekdays: 'WWW'}, lg: {weekdays: 'WWWW'} })"
        :locale="this.$i18n.locale"
        :from-page="{month: this.timesheetMonth, year: this.timesheetYear}"
        :attributes="attributes"
        disable-page-swipe
        :trim-weeks="$screens({ default: true, lg: false })"
      >

        <template #header>
          <div></div>
        </template>
        <template #header-left-button>
          <div></div>
        </template>
        <template #header-right-button>
          <div></div>
        </template>

        <template v-slot:day-content="{ day, attributes }">
          <template v-if="attributes && attributes.length">
            <template v-for="attr in attributes">
              <div
                type="button"
                @click="selectDay(attr.customData.date, attr.key)"
                class="h-100 d-flex flex-column px-2 pb-2"
                :class="[
                  {'selected-day': attr.customData.date === selectedDate},
                  {'bg-light': attr.customData.isHoliday === 'Yes' && attr.customData.date !== selectedDate}
                ]"
              >
                <div class="d-flex">
                  <p class="text-warning font-weight-bold mb-0 pr-1 pr-md-2">
                    <i v-if="attr.customData.Remarks" :class="[$getConst.icons.comment, 'mr-1']"></i>
                    <i v-if="hasOvertime(attr.customData)" :class="$getConst.icons.exclamation_circle"></i>
                  </p>
                  <p class="font-weight-bold text-dark ml-auto mb-0 pr-1 pr-md-2">{{ day.day }}</p>
                </div>
                <p
                  v-if="attr.customData.isHoliday === 'Yes'"
                  class="hours font-weight-bold d-none d-sm-inline-block badge-success mx-1 badge-pill my-auto font-size-sm font-weight-600"
                >
                  <template v-if="isDayFilled(attr.customData.std)">
                    {{ attr.customData.std | removeDecimalIfNotNecessary }}
                    <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                    <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                  </template>
                </p>
                <p
                  v-else-if="attr.customData.absence === '0'"
                  class="hours font-weight-bold badge-success text-white py-1 px-0 mx-1 badge-pill my-auto line-height-sm font-size-sm font-weight-600"
                >
                  {{ attr.customData.std | removeDecimalIfNotNecessary }}
                  <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                  <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                </p>

                <template v-else>
                  <p class="d-flex hours font-weight-bold badge-warning mx-1 badge-pill my-auto mx-1 text-uppercase font-size-sm font-weight-600 text-white">
                    <span v-if="parseFloat(attr.customData.absenceQty) === 0.5" class="font-size-md my-auto text-center"><sup>1</sup>&frasl;<sub>2</sub></span>
                    <span class="text-center">{{ getAbsenceDescription(attr.customData.absenceCode) }}</span>
                  </p>
                  <p
                    v-if="parseFloat(attr.customData.absenceQty) === 0.5"
                    class="hours font-weight-bold badge-success text-white py-1 px-0 mx-1 badge-pill my-auto line-height-sm font-size-sm font-weight-600"
                  >
                    {{ attr.customData.std | removeDecimalIfNotNecessary }}
                    <span v-if="timesheet.Unit.toLowerCase() === 'hour'">hrs</span>
                    <span v-else-if="timesheet.Unit.toLowerCase() === 'day'">days</span>
                  </p>
                </template>
              </div>
            </template>
          </template>
          <div v-else class="h-100 d-flex flex-column">
            <p class="font-weight-bold text-dark text-right mb-0 pr-1 pr-md-2">{{ day.day }}</p>
          </div>
        </template>

      </calendar>
    </b-col>

    <b-col id="timesheet-form" class="xxl-3">
      <TimesheetForm
        v-if="selectedDate"
        v-model="timesheet"
        :date="selectedDate"
        :day-index="selectedDayIndex"
        :is-mobile-view="isMobileView"
        @closeDayForm="deselectDay"
      />
      <TimesheetTotalHours
        v-else
        :timesheet="timesheet"
        submit-from-form
      />
    </b-col>
  </b-row>
</template>

<script>
  import TimesheetForm from "./TimesheetForm";
  import TimesheetTotalHours from "./TimesheetTotalHours";

  export default {
    name: "MonthlyTimesheet",
    components: {TimesheetTotalHours, TimesheetForm},
    props: {
      timesheet: null,
      isMobileView: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        masks: {
          weekdays: 'WWWW',
        },
        timesheetMonth: null,
        timesheetYear: null,
        approversNames: [],
        attributes: [],
        selectedDate: null,
        selectedDayIndex: null,
        updatedAbsence: []
      }
    },
    methods: {
      hasOvertime(dayData) {
        return parseFloat(dayData.ot1) > 0 || parseFloat(dayData.ot2) > 0 || parseFloat(dayData.ot3) > 0;
      },
      isDayFilled(std) {
        return parseFloat(std) > 0;
      },
      selectDay(date, index) {
        this.selectedDate = date;
        this.selectedDayIndex = index;
        const timesheetFormElement = document.getElementById("timesheet-form");
        timesheetFormElement.scrollIntoView();
      },
      deselectDay() {
        this.selectedDate = null;
        this.selectedDayIndex = null;
      },
      getAbsenceDescription(absenceCode) {
        const absenceType = this.timesheet.Days.Absences[0].AbsencesList.find(
          element => element.code === absenceCode
        );

        return absenceType.Description;
      },
      getTimesheet() {
        this.timesheetMonth = Number(this.timesheet.TimesheetStartDate.split("/")[1]);
        this.timesheetYear = this.timesheet.TimesheetStartDate.split("/")[2];
        this.formatDays();
        this.timesheet.Approvers.forEach(approver => {
          if (approver.id > 0) {
            this.approversNames.push(approver.name);
          }
        })
      },
      formatDays() {
        this.timesheet.Days.Days.forEach((day, key) => {
          let formattedDay = {};
          formattedDay.customData = {};
          const dateParts = day.date.split("/");
          formattedDay.key = key;
          formattedDay.dates = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
          formattedDay.customData = day;
          this.attributes.push(formattedDay);
        })
      },
    },
    created() {
      this.getTimesheet();
    }
  }
</script>

<style scoped>

</style>
